/**
 * Facility Offer Letter (FOL) new flow — status codes shared by the offer
 * letter (`crdt_fo_ltr`) and its documents (`crdt_fo_ltr_doc`).
 *
 * The labels normally come from the status picklists; `FO_LTR_STATUS_LABEL_KEYS`
 * is only the fallback for codes the picklists don't carry (4810–4816 are new).
 */
export const FO_LTR_STATUS = {
  INITIATED: 720,
  PENDING_RO: 2658,
  RECOMMENDATION_PENDING_VCM_DOC: 2689,
  WITH_AM: 2691,
  PRINTED: 4810,
  PENDING_RO_ACCEPTANCE: 4811,
  PENDING_SAC_REVIEW: 4812,
  RO_APPROVED_SIGNED_COPY_PENDING: 4813,
  RO_RECOMMENDATION_PENDING_AM: 4814,
  SIGNED_COPY_PENDING_RO: 4815,
  FOL_RECOMMENDATION_PENDING_VCM: 4816,
  APPROVED: 902,
  REJECTED: 765,
} as const;

/** Translation keys for the FOL status codes (fallback when the picklist has no label). */
export const FO_LTR_STATUS_LABEL_KEYS: Record<number, string> = {
  720: 'FACILITIES.FOL_STATUS.INITIATED',
  2658: 'FACILITIES.FOL_STATUS.PENDING_RO',
  2689: 'FACILITIES.FOL_STATUS.RECOMMENDATION_PENDING_VCM_DOC',
  2691: 'FACILITIES.FOL_STATUS.WITH_AM',
  4810: 'FACILITIES.FOL_STATUS.PRINTED',
  4811: 'FACILITIES.FOL_STATUS.PENDING_RO_ACCEPTANCE',
  4812: 'FACILITIES.FOL_STATUS.PENDING_SAC_REVIEW',
  4813: 'FACILITIES.FOL_STATUS.RO_APPROVED_SIGNED_COPY_PENDING',
  4814: 'FACILITIES.FOL_STATUS.RO_RECOMMENDATION_PENDING_AM',
  4815: 'FACILITIES.FOL_STATUS.SIGNED_COPY_PENDING_RO',
  4816: 'FACILITIES.FOL_STATUS.FOL_RECOMMENDATION_PENDING_VCM',
  902: 'FACILITIES.FOL_STATUS.APPROVED',
  765: 'FACILITIES.FOL_STATUS.REJECTED',
};

/** The Facility Offer Letter document itself (Stage 1). */
export const FO_LTR_DOC_SEQ = 36;

/** Letter of Hypothecation — its signed copy needs a charge type (charge row). */
export const FO_LTR_LOH_DOC_SEQ = 21;

/**
 * Issue-flag documents may be deleted only while at one of these statuses;
 * the offer letter document (36) never; every other document at any status.
 */
export const FO_LTR_ISSUE_DOC_DELETABLE_STATUSES: number[] = [
  FO_LTR_STATUS.PENDING_SAC_REVIEW,
  FO_LTR_STATUS.PENDING_RO,
  FO_LTR_STATUS.SIGNED_COPY_PENDING_RO,
];

/**
 * Role codes (localStorage `role`). The flow's "SA review" (Shariah
 * Advisory Committee — Mudarabah / Islamic-term documents) is LNLY_SAC.
 */
export const FO_LTR_ROLES = {
  AM: ['LNLY_AM'],
  RO: ['LNLY_RO'],
  TL: ['LNLY_TL'],
  VCM: ['LNLY_VCM'],
  SAC: ['LNLY_SAC'],
} as const;

/** The logged-in user's role code, upper-cased. */
export function currentFoLtrRole(): string {
  return (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
    .trim()
    .toUpperCase();
}

export function hasFoLtrRole(roles: readonly string[]): boolean {
  return roles.includes(currentFoLtrRole());
}

/**
 * save-doc payload for a signed copy (first upload on a 4813 row, or a
 * re-upload of a signed row at 4814), exactly as the backend expects:
 *   crdtFoLtrSeq, docSeq, sgndCpyFlg=1, crdtFoLtrDocSeq, sftCpyDt,
 *   hrdCpyDt, signingDt, file (+ chrgTyp only for the LOH, doc 21).
 * `crdtFoLtrDocSeq` is the 4813 row for a first signed copy, or the signed
 * row itself for a re-upload — the backend checks it belongs to this letter
 * and document type. aprvngDt, uploadDt and prntDocSeq are not sent.
 */
export function buildFoLtrSignedCopyFormData(params: {
  crdtFoLtrSeq: number;
  docSeq: number;
  crdtFoLtrDocSeq: number;
  sftCpyDt?: string | null;
  hrdCpyDt?: string | null;
  signingDt?: string | null;
  chrgTyp?: string | number | null;
  /** The row's `chrgTypReq` flag; when given it decides whether chrgTyp is sent. */
  chrgTypReq?: boolean;
  /** sgndCpyFlg sent with the upload; defaults to 1 (signed copy). */
  sgndCpyFlg?: 0 | 1;
  file: File;
}): FormData {
  const formData = new FormData();

  formData.append('crdtFoLtrSeq', String(params.crdtFoLtrSeq));
  formData.append('docSeq', String(params.docSeq));
  formData.append('sgndCpyFlg', String(params.sgndCpyFlg ?? 1));
  formData.append('crdtFoLtrDocSeq', String(params.crdtFoLtrDocSeq));

  if (params.sftCpyDt) {
    formData.append('sftCpyDt', params.sftCpyDt);
  }

  if (params.hrdCpyDt) {
    formData.append('hrdCpyDt', params.hrdCpyDt);
  }

  if (params.signingDt) {
    formData.append('signingDt', params.signingDt);
  }

  const chargeTypeAsked = params.chrgTypReq ?? Number(params.docSeq) === FO_LTR_LOH_DOC_SEQ;

  if (chargeTypeAsked && params.chrgTyp != null && params.chrgTyp !== '') {
    formData.append('chrgTyp', String(params.chrgTyp));
  }

  formData.append('file', params.file);

  return formData;
}
